<script>
import Cartesian from '../chart/Cartesian';
import style from '../chart/chart.scss';

export default {
	name: 'LineChart',

	mixins: [
		Cartesian,
	],

	props: {
		backgroundColor: {
			type: [Array, String],
			required: false,
			default: style.primary,
			validator: (value) => {
				const hexadecimalRegex = /^#([0-9A-F]{3}){1,2}$/i;
				const rgbRegex = /rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)/;

				return hexadecimalRegex.test(value) || rgbRegex.test(value);
			},
			description: 'Sets the line’s fill color below the line and in the legend (must receive either rgb or hex)',
		},

		backgroundFill: {
			type: [Array, String],
			required: false,
			default: 'none',
			validator: (value) => {
				const fillOptions = [
					'fill',
					'gradient',
					'none',
				];
				const isValidFill = (element) => fillOptions.indexOf(element) !== -1;

				return isValidFill(value) || (Array.isArray(value) && value.every(isValidFill));
			},
			description: 'Sets whether the space between the line and the 0 on the y axis will be filled with the backgroundColor',
		},

		backgroundTransparency: {
			type: [Array, Number],
			required: false,
			default: 0.5,
			description: 'Sets the line’s fill transparency below the line and in the legend',
		},

		lineColor: {
			type: [Array, String],
			required: false,
			default: style.primary,
			validator: (value) => {
				const hexadecimalRegex = /^#([0-9A-F]{3}){1,2}$/i;
				const rgbRegex = /rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)/;
				const rgbaRegex = /rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},?\s\d?\.?\d*\)/;

				return hexadecimalRegex.test(value) || rgbRegex.test(value) || rgbaRegex.test(value);
			},
			description: 'Sets the color of the line (must receive either rgb or hex)',
		},

		lineDashStyle: {
			type: Array,
			required: false,
			default: () => [[0, 0]],
			description: 'The first value sets the length in px of the dash and the second one sets the length in px of the space',
		},

		pointBorderWidth: {
			type: [Array, Number],
			required: false,
			default: 1,
			description: 'Sets the width of the point\'s border',
		},

		pointHoverBackground: {
			type: [Array, String],
			required: false,
			default: null,
			validator: (value) => {
				const hexadecimalRegex = /^#([0-9A-F]{3}){1,2}$/i;
				const rgbRegex = /rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)/;
				const rgbaRegex = /rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},?\s\d?\.?\d*\)/;

				return hexadecimalRegex.test(value)
					|| rgbRegex.test(value)
					|| rgbaRegex.test(value)
					|| null;
			},
			description: 'Sets the color of the line (must receive either rgb or hex)',
		},

		pointHoverRadius: {
			type: [Array, Number],
			required: false,
			default: 5,
			description: 'Sets the size of the point on hover. A value of 0 removes the point.',
		},

		pointRadius: {
			type: [Array, Number],
			required: false,
			default: 0,
			description: 'Sets the default size of the point. A value of 0 removes the point.',
		},

		pointStyle: {
			type: [Array, String],
			required: false,
			default: 'circle',
			validator: (value) => {
				const styles = [
					'circle',
					'cross',
					'crossRot',
					'dash',
					'line',
					'rect',
					'rectRounded',
					'rectRot',
					'star',
					'triangle',
				];
				const isValidStyle = (element) => styles.indexOf(element) !== -1;

				return isValidStyle(value) || (Array.isArray(value) && value.every(isValidStyle));
			},
			description: 'Sets shape of the point',
		},

		tension: {
			type: Number,
			required: false,
			default: 0.2,
			description: 'Sets the tension/curvature of the line',
		},
	},

	data() {
		return {
			borderWidth: 2,
		};
	},

	computed: {
		borderOptions() {
			const borderOptions = {};

			if (typeof this.lineDashStyle === 'number') {
				borderOptions.borderDash = [
					this.lineDashStyle,
					this.lineDashStyle,
				];
			} else {
				borderOptions.borderDash = this.lineDashStyle;
			}

			if (this.backgroundColor) {
				borderOptions.backgroundColor = this.computedBackgroundColor;
				borderOptions.fill = true;
			}

			return borderOptions;
		},

		computedBackgroundColor() {
			const isRGB = this.isRGB(this.backgroundColor);
			let background = this.backgroundColor;

			// check if background is a string or an array
			// find the rgb values and create the rgba values
			if (Array.isArray(background) && background.length > 1) {
				background = background.map((color, idx) => {
					if (isRGB) {
						return `rgba(${color.slice(4, -1)}, ${this.backgroundTransparency[idx]})`;
					}
					return color;
				});
			} else if (typeof background === 'string' && isRGB) {
				background = `rgba(${this.backgroundColor.slice(4, -1)}, ${this.backgroundTransparency})`;
			}

			return background;
		},

		data() {
			const datasetArray = this.chartData.datasets.map((dataset, index) => {
				const keys = Object.keys(this.dataOptions);
				const values = Object.values(this.dataOptions);

				// we select the appropriate values when more than one is provided
				// by graphs with multiple datasets
				const newValues = values.map(value => {
					// if the value is an array,
					// return the value at the appropriate index
					if (Array.isArray(value) && value.length > 1) {
						return value[index];
					}
					// in the case of the dashed line value,
					// we need to flatten the array,
					return Array.isArray(value) ? value.flat() : value;
				});

				// we reassemble the keys and values into the correct data object
				const newDataOptions = keys.reduce((obj, key, idx) => ({
					...obj,
					[key]: newValues[idx],
				}), {});

				// isolate rgba color
				const rgbaColor = Array.isArray(this.computedBackgroundColor)
					? this.computedBackgroundColor[index]
					: this.computedBackgroundColor;

				const newDataset = dataset.reduce(
					(acc, curr) => {
						acc.data.push(curr.data || 0);

						return acc;
					}, {
						data: [],
						backgroundColor: rgbaColor,
						barPercentage: 1,
						borderWidth: 0,
						...newDataOptions,
					},
				);
				return newDataset;
			});

			return {
				datasets:
					// eslint-disable-next-line arrow-body-style
					datasetArray.map((dataset, index) => {
						return {
							...dataset,
							backgroundColor: (context) => {
								let fill = this.backgroundFill;
								if (typeof fill === 'object' && fill.length > 1) {
									fill = this.backgroundFill[index];
								}

								if (fill === 'gradient') {
									const { chart } = context;
									const { ctx, chartArea } = chart;

									if (!chartArea) {
										return;
									}

									// eslint-disable-next-line consistent-return
									return this.gradientBackgroundColor(ctx, chartArea, index);
								}

								if (fill === 'fill') {
									// eslint-disable-next-line consistent-return
									return dataset.backgroundColor;
								}

								// eslint-disable-next-line consistent-return
								return 'transparent';
							},
							label: (
								this.lineLabel
								&& this.lineLabel[index]
								&& this.lineLabel.length === this.chartData.datasets.length
							)
								? this.lineLabel[index]
								: '',
							pointBackgroundColor: dataset.borderColor,
						};
					}),
				labels: this.chartData.datasets[0].map(item => item.datasetLabel || ''),
			};
		},

		dataOptions() {
			return {
				borderColor: this.lineColor,
				borderWidth: this.borderWidth,
				label: this.lineLabel,
				pointBorderColor: this.pointHoverBackground || this.lineColor,
				pointBorderWidth: this.pointBorderWidth,
				hoverBackgroundColor: this.pointHoverBackground || this.lineColor,
				hoverRadius: this.pointHoverRadius,
				pointRadius: this.pointRadius,
				pointStyle: this.pointStyle,
				tension: this.tension,
				...this.borderOptions,
			};
		},

		chartOptions() {
			return {
				...this.cartesianOptions,
			};
		},
	},

	created() {
		this.chartId = 'line';
		this.chartType = 'line';
	},

	methods: {
		gradientBackgroundColor(ctx, chartArea, index) {
			let gradient = null;

			gradient = ctx.createLinearGradient(0, 0, 0, chartArea.height);
			const rgb = (typeof this.backgroundColor === 'string'
				? this.backgroundColor
				: this.backgroundColor[index]);

			const transparency = Array.isArray(this.backgroundTransparency)
				? this.backgroundTransparency[index]
				: this.backgroundTransparency;
			const stop0 = `rgba(${rgb.slice(4, -1)}, ${transparency})`;
			const stop1 = `rgba(${rgb.slice(4, -1)}, 0)`;

			gradient.addColorStop(0, stop0);
			// we use an rgba value here 🠋 to prevent grey tones in the gradient
			gradient.addColorStop(1, stop1);

			return gradient;
		},
	},
};
</script>

<style lang="scss" scoped src="./line-chart.scss" />
